/** Move, resize and re-layer what draws on one canvas (`OVL-027`). */

import { useCallback, useEffect, useRef, useState } from "react";

import type { AddedActivityRow } from "../types";

import type { Locks } from "./LayersPanel";

import {
  FULL_CANVAS,
  GRIPS,
  dragged,
  keyed,
  type DragOptions,
  type Grip,
  type Placement,
} from "./lib/arrangement";
import { droppedAt, pointIn } from "./lib/drop";
import { useStageClaim } from "./lib/stageClaim";

/** One gesture in flight. Held in a ref, so no re-render between two pointer events can lose it. */
interface Drag {
  addedActivityId: string;
  grip: Grip;
  from: { x: number; y: number };
  /** The stage box measured once, so a re-layout mid-drag cannot change the scale under it. */
  frame: { width: number; height: number };
  start: Placement;
  moved: boolean;
}

/** How long the stage stays quiet before the whole-canvas replace goes out. */
const SETTLE_MS = 250;

/** Far enough for a press to have been a drag rather than a click, in stage pixels. */
const SLOP_PX = 2;

/** What the keys held during a resize ask for: Shift keeps the ratio, Alt holds the centre. */
function modifiers(event: { shiftKey: boolean; altKey: boolean }): DragOptions {
  return { aspect: event.shiftKey, centre: event.altKey };
}

/**
 * The rectangle this gesture starts from.
 *
 * A never-sized layer fills the canvas, so moving one clamps straight back and writes nothing.
 * Dragging one gives it the same first rectangle a drop gives it, centred where the press was,
 * and the drag carries on from there.
 */
function startOf(
  held: Placement | undefined,
  grip: Grip,
  box: DOMRect,
  at: { clientX: number; clientY: number },
): Placement {
  if (held !== undefined) return held;
  if (grip !== "move") return FULL_CANVAS;
  const point = pointIn(box, at.clientX, at.clientY);
  return point === null ? FULL_CANVAS : droppedAt(undefined, point);
}

export interface ArrangeLayerProps {
  /** The activities drawn on this canvas, back to front. */
  readonly rows: readonly AddedActivityRow[];
  readonly placements: Readonly<Record<string, Placement>>;
  /** A locked layer selects but never moves. */
  readonly locks: Locks;
  readonly selectedId: string | null;
  readonly onSelect: (addedActivityId: string) => void;
  /** Called on every pointer move, so the stage redraws while the drag is happening. */
  readonly onDraft: (placements: Record<string, Placement> | null) => void;
  readonly onCommit: (placements: Record<string, Placement>) => void;
}

export function ArrangeLayer({
  rows,
  placements,
  locks,
  selectedId,
  onSelect,
  onDraft,
  onCommit,
}: ArrangeLayerProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const claim = useStageClaim();

  // The gesture lives in a ref, never in state. Selecting a layer moves the address, which
  // re-renders this whole subtree between the pointerdown and the first pointermove, and a
  // gesture held in state reads back as null there: the press selects and nothing ever moves.
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState(false);

  // A drag and a held arrow key both write the same whole-canvas replace, so the write waits for
  // the quiet rather than going out once per pointerup and once per keystroke.
  const timer = useRef<number | null>(null);
  const owed = useRef<Record<string, Placement> | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const draftRef = useRef(onDraft);
  draftRef.current = onDraft;
  const placementsRef = useRef(placements);
  placementsRef.current = placements;

  const flush = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const held = owed.current;
    owed.current = null;
    if (held !== null) commitRef.current(held);
  }, []);

  const settle = useCallback(
    (next: Record<string, Placement>) => {
      owed.current = next;
      draftRef.current(next);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        flush();
      }, SETTLE_MS);
    },
    [flush],
  );

  // Leaving the tool must not lose the last edit.
  useEffect(
    () => () => {
      flush();
    },
    [flush],
  );

  /** The rectangle this pointer position asks for, against the box measured when the drag began. */
  const rectangleOf = useCallback(
    (held: Drag, event: PointerEvent): Record<string, Placement> => {
      const next = dragged(
        held.start,
        held.grip,
        (event.clientX - held.from.x) / held.frame.width,
        (event.clientY - held.from.y) / held.frame.height,
        modifiers(event),
      );
      return { ...placementsRef.current, [held.addedActivityId]: next };
    },
    [],
  );

  // The gesture is followed on the window rather than on this overlay. The pointer leaves the box
  // on the first move and leaves the stage on a long one, and a listener on either stops there.
  useEffect(() => {
    const move = (event: PointerEvent): void => {
      const held = drag.current;
      if (held === null) return;
      if (
        Math.abs(event.clientX - held.from.x) > SLOP_PX ||
        Math.abs(event.clientY - held.from.y) > SLOP_PX
      ) {
        held.moved = true;
      }
      if (!held.moved) return;
      draftRef.current(rectangleOf(held, event));
    };

    const finish = (event: PointerEvent): void => {
      const held = drag.current;
      if (held === null) return;
      drag.current = null;
      setDragging(false);
      if (!held.moved) return;
      settle(rectangleOf(held, event));
    };

    const cancel = (): void => {
      if (drag.current === null) return;
      drag.current = null;
      setDragging(false);
      draftRef.current(null);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
    };
  }, [rectangleOf, settle]);

  const begin = useCallback(
    (event: React.PointerEvent, addedActivityId: string, grip: Grip) => {
      if (event.button > 0) return;
      // The stage selects the space on any click no layer claimed. This press is on a layer, so
      // claim it: without this every gesture ends by deselecting what it just started from.
      claim();
      event.preventDefault();
      event.stopPropagation();
      onSelect(addedActivityId);
      if (locks[addedActivityId] === true) return;
      const box = frameRef.current?.getBoundingClientRect();
      if (box === undefined || box.width === 0 || box.height === 0) return;
      drag.current = {
        addedActivityId,
        grip,
        from: { x: event.clientX, y: event.clientY },
        frame: { width: box.width, height: box.height },
        start: startOf(placements[addedActivityId], grip, box, event),
        moved: false,
      };
      setDragging(true);
    },
    [claim, onSelect, placements, locks],
  );

  return (
    <div
      className={dragging ? "cx-arrange cx-arrange-live" : "cx-arrange"}
      ref={frameRef}
      onClick={(event) => {
        // A click that landed on a layer is not a click on empty canvas.
        if (event.target !== event.currentTarget) claim();
      }}
    >
      {rows.map((row) => {
        const held = placements[row.added_activity_id] ?? FULL_CANVAS;
        if (held.hidden) return null;
        const chosen = selectedId === row.added_activity_id;
        const locked = locks[row.added_activity_id] === true;
        return (
          <div
            key={row.added_activity_id}
            className={[
              "cx-arrange-box",
              chosen ? "cx-arrange-box-on" : "",
              locked ? "cx-arrange-box-locked" : "",
            ]
              .filter((one) => one !== "")
              .join(" ")}
            style={{
              left: `${String(held.left * 100)}%`,
              top: `${String(held.top * 100)}%`,
              width: `${String(held.width * 100)}%`,
              height: `${String(held.height * 100)}%`,
            }}
            role="button"
            tabIndex={0}
            aria-label={`Arrange ${row.name}`}
            aria-pressed={chosen}
            onPointerDown={(event) => {
              begin(event, row.added_activity_id, "move");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                onSelect(row.added_activity_id);
                return;
              }
              if (locked) return;
              const next = keyed(held, event.key, {
                coarse: event.shiftKey,
                resize: event.altKey,
              });
              if (next === null) return;
              event.preventDefault();
              onSelect(row.added_activity_id);
              settle({ ...placements, [row.added_activity_id]: next });
            }}
          >
            <span className="cx-arrange-name">{row.name}</span>
            {chosen && !locked
              ? GRIPS.map((grip) => (
                  <span
                    key={grip}
                    className={`cx-arrange-grip cx-arrange-grip-${grip}`}
                    onPointerDown={(event) => {
                      begin(event, row.added_activity_id, grip);
                    }}
                  />
                ))
              : null}
          </div>
        );
      })}
    </div>
  );
}
